"use client";

import { cn } from "@/lib/utils";

export interface SearchInputAuroraGlowProps {
  /** `compact` = anillo y halo más pequeños (hover en barra cerrada). */
  size?: "default" | "compact";
  /** Visible solo con `:hover` en `.search-bar-trigger`. */
  showOnHover?: boolean;
}

/** Anillo Aurora multicolor alrededor del input de búsqueda. */
export function SearchInputAuroraGlow({
  size = "default",
  showOnHover = false
}: SearchInputAuroraGlowProps) {
  return (
    <>
      <div
        aria-hidden
        className={cn(
          "search-aurora-glow",
          size === "compact" && "search-aurora-glow--compact",
          showOnHover && "search-aurora-glow--hover-only"
        )}
      />
      <div
        aria-hidden
        className={cn(
          "search-aurora-halo",
          size === "compact" && "search-aurora-halo--compact",
          showOnHover && "search-aurora-halo--hover-only"
        )}
      />
      <style jsx global>{`
        @property --search-glow-angle {
          syntax: "<angle>";
          initial-value: 0deg;
          inherits: false;
        }
        @keyframes search-glow-rotate {
          to { --search-glow-angle: 360deg; }
        }
        .search-aurora-glow {
          position: absolute;
          inset: 0;
          z-index: 0;
          border-radius: inherit;
          padding: 2px;
          background: conic-gradient(
            from var(--search-glow-angle),
            #22d3ee, #fb923c, #a78bfa, #ec4899, #10b981, #6366f1, #22d3ee
          );
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask-composite: exclude;
          opacity: 0;
          transition: opacity 0.3s ease;
          animation: search-glow-rotate 5s linear infinite;
        }
        .search-aurora-glow--compact { padding: 2px; }
        .search-aurora-halo {
          position: absolute;
          inset: -4px;
          z-index: -1;
          border-radius: inherit;
          background: conic-gradient(
            from var(--search-glow-angle),
            rgba(34,211,238,0.42), rgba(251,146,60,0.38), rgba(167,139,250,0.42),
            rgba(236,72,153,0.38), rgba(16,185,129,0.38), rgba(99,102,241,0.42),
            rgba(34,211,238,0.42)
          );
          filter: blur(8px);
          opacity: 0;
          transition: opacity 0.3s ease;
          animation: search-glow-rotate 7s linear infinite reverse;
        }
        .search-aurora-halo--compact { inset: -3px; filter: blur(7px); }
        .search-aurora-glow--hover-only,
        .search-aurora-halo--hover-only { opacity: 0; }
        .search-bar-trigger .search-aurora-glow--hover-only { opacity: 0.22; }
        .search-bar-trigger .search-aurora-halo--hover-only { opacity: 0.16; }
        .search-bar-trigger:hover .search-aurora-glow--hover-only { opacity: 0.72; }
        .search-bar-trigger:hover .search-aurora-halo--hover-only { opacity: 0.58; }
        .search-bar-trigger-shell { position: relative; border-radius: 9999px; }
        .search-expand-pill--expanded .search-aurora-glow {
          opacity: 1;
          padding: 2px;
          transition: opacity 0.4s ease, padding 0.4s ease;
        }
        .search-expand-pill--expanded .search-aurora-halo {
          inset: -2px;
          filter: blur(5px);
          opacity: 1;
          background: conic-gradient(
            from var(--search-glow-angle),
            rgba(34,211,238,0.92), rgba(251,146,60,0.84), rgba(167,139,250,0.92),
            rgba(236,72,153,0.84), rgba(16,185,129,0.84), rgba(99,102,241,0.92),
            rgba(34,211,238,0.92)
          );
          transition: opacity 0.4s ease, filter 0.4s ease;
        }
        @media (prefers-reduced-motion: reduce) {
          .search-aurora-glow, .search-aurora-halo { animation: none; }
        }
      `}</style>
    </>
  );
}
